import React, { ReactNode } from 'react';
import { Button } from './ui/Button';
import { staticUrl } from '../utils';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  gotoPtEApp: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * ErrorBoundary component - Catches JavaScript errors anywhere in the child component tree
 * Logs those errors, and displays a fallback UI instead of crashing the entire app
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    console.log('ErrorBoundary State Update - hasError: true');

    this.setState({
      error,
      errorInfo,
    });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className='flex items-center justify-center min-h-screen bg-gray-50'
          data-pte-name='error-boundary'
        >
          <div
            className='max-w-full w-[900px] p-6 bg-white rounded-lg shadow-md'
            data-pte-name='error-boundary-container'
          >
            <div className='text-center' data-pte-name='error-boundary-message'>
              <div className='flex justify-center mb-4'>
                <img
                  src={staticUrl('icons/featured-error-outline-md.svg')}
                  alt='Error Icon'
                />
              </div>
              <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                Something went wrong
              </h1>
              <p className='text-gray-600 mb-6'>
                We're sorry for the inconvenience. Please try refreshing the
                page or contact support if the problem persists.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div
                  className='mb-6 p-4 bg-red-50 border border-red-200 rounded'
                  data-pte-name='error-boundary-dev-info'
                >
                  <p className='text-sm font-mono text-red-600 text-left break-words'>
                    <strong>Error:</strong> {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className='mt-4'>
                      <summary className='cursor-pointer text-sm font-semibold text-red-600'>
                        Error Stack
                      </summary>
                      <pre className='mt-2 text-xs text-red-600 overflow-auto bg-red-100 p-2 rounded text-left'>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className='text-center gap-3'>
                <div
                  className='w-[300px] max-w-full mx-auto flex gap-3'
                  data-pte-name='error-boundary-actions'
                >
                  <Button
                    variant='tertiary'
                    intent='cta'
                    onClick={this.handleReset}
                    className='flex-1 px-4 py-2 text-primary-default'
                    data-pte-name='error-boundary-tryagain-btn'
                  >
                    Try again
                  </Button>
                  <Button
                    variant='primary'
                    intent='cta'
                    onClick={() => {
                      this.props.gotoPtEApp();
                    }}
                    className='flex-1 px-4 py-2'
                    data-pte-name='error-boundary-goto-app-btn'
                  >
                    Go to PtEverywhere
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
